/**
 * BatteryLevel - 电量显示组件
 *
 * 未充电：iconfont（power-25/50/75/100）；≤37.5% 为红色
 * 充电中：shared-assets/common 下 SVG（charge25/50/75/100，自带颜色）
 */

import React from "react";
import { useTranslation } from "react-i18next";
import {
  charge25Img,
  charge50Img,
  charge75Img,
  charge100Img,
} from "@eweadn-qiankun/shared-assets";
import { Iconfont } from "../Iconfont";
import styles from "./BatteryLevel.module.css";

/** 展示用电量：四舍五入并限制在 1–100 */
function clampDisplayBatteryLevel(level: number): number {
  const n = Number(level);
  if (!Number.isFinite(n)) return 1;
  return Math.min(100, Math.max(1, Math.round(n)));
}

export interface BatteryLevelProps {
  /** 电量百分比（展示时夹紧为 1–100） */
  level: number;
  /** 是否正在充电 */
  charging?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 是否显示百分比文字 */
  showPercentage?: boolean;
  /** 是否显示电量图标 */
  showIcon?: boolean;
}

const BATTERY_TIER_LOW = 37.5;
const BATTERY_TIER_MID = 62.5;
const BATTERY_TIER_HIGH = 87.5;

type BatteryTier = "25" | "50" | "75" | "100";

const CHARGE_ICON_BY_TIER: Record<BatteryTier, string> = {
  "25": charge25Img,
  "50": charge50Img,
  "75": charge75Img,
  "100": charge100Img,
};

function getBatteryTier(level: number): BatteryTier {
  if (level <= BATTERY_TIER_LOW) return "25";
  if (level <= BATTERY_TIER_MID) return "50";
  if (level <= BATTERY_TIER_HIGH) return "75";
  return "100";
}

const BatteryLevel: React.FC<BatteryLevelProps> = ({
  level,
  charging = false,
  className = "",
  showPercentage = true,
  showIcon = true,
}) => {
  const { t } = useTranslation();
  const displayLevel = clampDisplayBatteryLevel(level);
  const tier = getBatteryTier(displayLevel);
  const isLowBattery = !charging && displayLevel <= BATTERY_TIER_LOW;

  return (
    <div
      className={`${styles.battery} ${isLowBattery ? styles.batteryLow : ""} ${charging ? styles.batteryCharging : ""} ${className}`}
    >
      {showIcon && (
        <div className={styles.batteryIcon}>
          {charging ? (
            <img
              src={CHARGE_ICON_BY_TIER[tier]}
              alt=""
              className={styles.chargingSvg}
            />
          ) : (
            <Iconfont icon={`power-${tier}`} />
          )}
        </div>
      )}
      {charging && !showPercentage ? (
        <span className={styles.batteryLevel}>
          {t("device.status.charging")}
        </span>
      ) : (
        showPercentage && (
          <span className={styles.batteryLevel}>{displayLevel}%</span>
        )
      )}
    </div>
  );
};

export { BatteryLevel };
